import React, { memo } from 'react';
import { FieldValues } from 'react-hook-form';
import cn from 'classnames';

import { ITextAreaProps } from './TextArea.types';

import Label from '../Label';
import Meta from '../Meta';

import './TextArea.scss';

const TextArea = <T extends FieldValues>({
	className,
	disabled,
	errorMessage,
	isNotResizable = false,
	isTransparent = false,
	label,
	maxLength,
	isMaxLengthCounter,
	name,
	onChange,
	placeholder,
	readonly,
	register,
	required,
	rows,
	setValue,
	value,
	valueLength,
	withUseForms,
	textAreaClassName,
	isWithoutFocus,
}: ITextAreaProps<T>) => {
	const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		if (onChange) {
			onChange(e.target.value);
		} else if (setValue && name) {
			setValue(name, e.target.value);
		}
	};

	const textAreaClassname = cn('f5gTextArea', textAreaClassName, {
		'f5gTextAreaError': !!errorMessage,
		'f5gTextAreaResize': isNotResizable,
		'f5gTextAreaTransparent': isTransparent,
		'f5gTextAreaWithDisableStyle': !!disabled,
		'f5gTextAreaWithoutFocusStyle': isWithoutFocus,
	});

	return (
		<div className={cn(className, 'f5gTextAreaContainer')}>
			{label && (
				<Label
					className='f5gTextAreaLabel'
					label={label}
					required={required}
				/>
			)}
			<div>
				{withUseForms
					? <textarea
						aria-label={name}
						className={textAreaClassname}
						disabled={disabled}
						maxLength={maxLength}
						onInput={handleChange}
						placeholder={placeholder}
						readOnly={readonly}
						rows={rows}
						value={value}
						{...(register ? register(name) : {})}
					/>
					: <textarea
						aria-label={name}
						className={textAreaClassname}
						data-testid='text-area'
						disabled={disabled}
						maxLength={maxLength}
						name={name}
						onChange={handleChange}
						placeholder={placeholder}
						readOnly={readonly}
						rows={rows}
						value={value}
					/>
				}
			</div>
			<div className='f5gTextAreaMetaContainer'>
				{errorMessage && <Meta message={errorMessage as string} />}
				{isMaxLengthCounter && maxLength
					&& <div className={cn('f5gTextAreaCharCounter', {
						'f5gTextAreaCharCounterError': valueLength && valueLength > maxLength,
					})}>
						{`${valueLength || 0}/${maxLength}`}
					</div>
				}
			</div>
		</div>
	);
};

export default memo(TextArea) as <T extends FieldValues>(props: ITextAreaProps<T>) => JSX.Element;
